/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

/**
 * This hook provides an API for getting calculated cart items data.
 */

import { useState, useCallback, useMemo } from 'preact/compat';
import { CartModel } from '../data/models';
import { useText } from '@adobe-commerce/elsie/i18n';
import { state } from '@/cart/lib/state';
import { PriceProps } from '@adobe-commerce/elsie/components/Price';
import { updateProductsFromCart } from '@/cart/api';
import { debounce } from '@adobe-commerce/elsie/lib';


/**
 * Params for the useCartItems hook
 * 
 * @param {
 *  dictionary: ReturnType<typeof useText>;
 *  onQuantityUpdate?: (item: CartModel['items'][number], quantity: number) => void;
 *  onItemRemove?: (item: CartModel['items'][number]) => void;
 * }
 * 
 * dictionary - The dictionary
 * onQuantityUpdate - The function to call when the quantity is updated
 * onItemRemove - The function to call when the item is removed
 */
export interface useCartItemsProps {
  dictionary: ReturnType<typeof useText>;
  onQuantityUpdate?: (item: CartModel['items'][number], quantity: number) => void;
  onItemRemove?: (item: CartModel['items'][number]) => void;
}

/**
 * Params for the getConfiguration function
 */
interface GetConfigurationParams {
  item: CartModel['items'][number];
}

interface ItemUpdateState {
  isUpdating: boolean;
  updatedValue: any;
}

/**
 * Type for the API returned by useCartItems
 *
 * This interface ensures that the returned object from the hook is well-typed and clear for consumers.
 */
export interface UseCartItemsApi {
  showIncludedTaxPrice: boolean; // Whether to show prices including tax
  showExcludingTaxPrice: boolean; // Whether to show prices excluding tax
  itemsUpdating: Map<string, ItemUpdateState>; // Tracks which items are currently being updated
  itemUpdateErrors: Map<string, string>; // Tracks update errors for each item
  getConfiguration: (params: GetConfigurationParams) => object | undefined; // Returns configuration details for a cart item
  getPriceProps: (item: CartModel['items'][number]) => PriceProps; // Returns price display props for a cart item
  getSubtotalProps: (item: CartModel['items'][number]) => { subtotalProps: PriceProps; subtotalDiscountProps: PriceProps | null }; // Returns subtotal and discount subtotal props
  processQuantityChange: (item: CartModel['items'][number], value: number) => Promise<void>; // Handles quantity change with state and error management
  debouncedQuantityChange: (item: CartModel['items'][number], value: number) => void; // Debounced version of processQuantityChange
  setItemsUpdating: (itemsUpdating: Map<string, ItemUpdateState>) => void; // Allows external setting of updating state
  getWarningMessage: (item: CartModel['items'][number]) => string | undefined; // Returns warning message for a cart item
  setItemUpdateError: (uid: string, error: string) => void; // Sets or clears update error for an item
  handleRemoveItem: (item: CartModel['items'][number]) => Promise<CartModel | null>; // Removes an item from the cart
  setItemUpdating: (uid: string, state: boolean) => void; // Sets updating state for a specific item
}

/**
 * useCartItems hook
 *
 * Provides an API for managing and retrieving calculated cart items data, including price, subtotal, configuration, quantity updates, and error handling.
 *
 * @param {Object} params
 * @param {ReturnType<typeof useText>} params.dictionary - The i18n dictionary
 * @param {(item: CartModel['items'][number], quantity: number) => void} [params.onQuantityUpdate] - Callback when item quantity is updated
 * @param {(item: CartModel['items'][number]) => void} [params.onItemRemove] - Callback when item is removed
 *
 * @returns {Object} API for cart item management:
 *   - showIncludedTaxPrice: boolean
 *   - showExcludingTaxPrice: boolean
 *   - itemsUpdating: Map<string, ItemUpdateState>
 *   - itemUpdateErrors: Map<string, string>
 *   - getConfiguration(item): object | undefined
 *   - getPriceProps(item): PriceProps
 *   - getSubtotalProps(item): { subtotalProps: PriceProps, subtotalDiscountProps: PriceProps | null }
 *   - processQuantityChange(item, value): Promise<void>
 *   - debouncedQuantityChange(item, value): void
 *   - setItemsUpdating(map): void
 *   - getWarningMessage(item): string | undefined
 *   - setItemUpdateError(uid, error): void
 *   - handleRemoveItem(item): Promise<CartModel>
 *   - setItemUpdating(uid, state): void
 */
export const useCartItems = ({ dictionary, onQuantityUpdate, onItemRemove }: useCartItemsProps): UseCartItemsApi => {
  // State for tracking which items are being updated (e.g., quantity change in progress)
  const [itemsUpdating, setItemsUpdating] = useState<Map<string, ItemUpdateState>>(new Map());
  // State for tracking errors related to item updates
  const [itemUpdateErrors, setItemUpdateErrors] = useState<Map<string, string>>(new Map());

  // Get cart display settings (e.g., tax display mode) from global state
  const cartSettings = state?.config?.shoppingCartDisplaySetting;

  // Determine which price display modes are active
  const showIncludedTaxPrice = cartSettings?.price === 'INCLUDING_TAX';
  const showExcludingTaxPrice = cartSettings?.price === 'INCLUDING_EXCLUDING_TAX';

  /**
   * Returns a configuration object for the cart item, combining bundle, selected, and customizable options, as well as recipient/sender info.
   * Returns undefined if there is no configuration to show.
   */
  const getConfiguration = ({ item }: GetConfigurationParams) => {
    // Merge all possible configuration sources into a single object
    const configuration = {
      ...item.bundleOptions,
      ...item.selectedOptions,
      ...item.customizableOptions,
      // Add recipient/sender info if present
      ...(item.recipient ? { [dictionary.recipient]: item.recipient } : null),
      ...(item.recipientEmail && item.recipient
        ? {
          [dictionary.recipient]: `${item.recipient} (${item.recipientEmail})`,
        }
        : null),
      ...(item.sender ? { [dictionary.sender]: item.sender } : null),
      ...(item.senderEmail && item.sender
        ? {
          [dictionary.sender]: `${item.sender} (${item.senderEmail})`,
        }
        : {}),
      ...(item.message ? { [dictionary.message]: item.message } : null),
      // Add file/link info if present
      ...(item.links && item.links.count
        ? item.links.count > 1
          ? {
            [dictionary.files.replace(
              '{count}',
              item.links.count.toString()
            )]: item.links.result,
          }
          : {
            [dictionary.file.replace('{count}', item.links.count.toString())]:
              item.links.result,
          }
        : null),
    };

    // If no configuration, return undefined
    if (Object.keys(configuration).length === 0) {
      return undefined;
    }

    return configuration;
  };

  /**
   * Returns price props for the cart item, depending on tax and discount settings.
   * Used for rendering the price in the UI.
   */
  const getPriceProps = (item: CartModel['items'][number]) => {
    return showIncludedTaxPrice
      ? item.discounted
        ? {
          amount: item.regularPrice.value,
          currency: item.regularPrice.currency,
          'data-testid': 'discounted-regular-item-price',
        }
        : {
          amount: item.taxedPrice?.value,
          currency: item.taxedPrice?.currency,
          'data-testid': 'taxed-item-price',
        }
      : {
        amount: item.regularPrice?.value,
        currency: item.regularPrice?.currency,
        'data-testid': 'regular-item-price',
      };
  };

  /**
   * Returns subtotal and discount subtotal props for the cart item.
   * Used for rendering the subtotal and any discount in the UI.
   */
  const getSubtotalProps = (item: CartModel['items'][number]) => {
    const subtotalProps: PriceProps = {
      'aria-label': dictionary.regularPrice,
    };
    // If the item is discounted, prepare a separate object for the discount subtotal
    const subtotalDiscountProps: PriceProps | null = item.discounted ? {} : null;

    if (
      ['INCLUDING_TAX', 'INCLUDING_EXCLUDING_TAX'].includes(
        cartSettings?.price as string
      )
    ) {
      subtotalProps['amount'] = item.rowTotalIncludingTax?.value;
      subtotalProps['currency'] = item.rowTotalIncludingTax?.currency;
      subtotalProps['variant'] = item.discounted ? 'strikethrough' : 'default';
      // @ts-ignore: data-testid is used for testing
      subtotalProps['data-testid'] = 'including-tax-item-total';

      if (subtotalDiscountProps) {
        // If discounted, show the original total as the discount
        subtotalProps['amount'] = item.total?.value;
        subtotalProps['currency'] = item.total?.currency;

        subtotalDiscountProps['amount'] = item.rowTotalIncludingTax?.value;
        subtotalDiscountProps['currency'] = item.rowTotalIncludingTax?.currency;
        subtotalDiscountProps['sale'] = true;
        subtotalDiscountProps['aria-label'] = dictionary.discountedPrice;
        // @ts-ignore: data-testid is used for testing
        subtotalDiscountProps['data-testid'] = 'discount-total';
      }
    } else {
      subtotalProps['amount'] = item.total?.value;
      subtotalProps['currency'] = item.total?.currency;
      subtotalProps['variant'] = item.discounted ? 'strikethrough' : 'default';
      // @ts-ignore: data-testid is used for testing
      subtotalProps['data-testid'] = 'regular-item-total';

      if (subtotalDiscountProps) {
        subtotalDiscountProps['amount'] = item.discountedTotal?.value;
        subtotalDiscountProps['currency'] = item.discountedTotal?.currency;
        subtotalDiscountProps['sale'] = true;
        subtotalDiscountProps['aria-label'] = dictionary.regularPrice;
        // @ts-ignore: data-testid is used for testing
        subtotalDiscountProps['data-testid'] = 'discount-total';
      }
    }

    return {
      subtotalProps,
      subtotalDiscountProps,
    };
  };

  /**
   * Returns a warning message for the cart item, based on update errors, insufficient quantity, or low inventory.
   * Returns undefined if there is no warning.
   */
  const getWarningMessage = (item: CartModel['items'][number]) => {
    // Check for update error first
    const updateErrorMessage = itemUpdateErrors.get(item.uid);

    // Check for insufficient quantity
    const insufficientQuantityText = item.insufficientQuantity && item.stockLevel
      ? item.stockLevel === 'noNumber'
        ? dictionary.insufficientQuantityGeneral // No stock level set
        : dictionary.insufficientQuantity // Stock level set
          .replace('{inventory}', item.stockLevel?.toString())
          .replace('{count}', item.quantity.toString())
      : undefined; // Could not generate insufficient quantity text

    // Check for low inventory
    const lowInventoryText = item.lowInventory &&
      item.onlyXLeftInStock &&
      dictionary.lowInventory.replace(
        '{count}',
        item.onlyXLeftInStock?.toString()
      );

    // Return the first warning found, or undefined
    return updateErrorMessage || insufficientQuantityText || lowInventoryText || undefined;
  };

  /**
   * Processes a quantity change for the cart item, updating state and handling errors.
   * Handles optimistic UI updates and error state.
   */
  const processQuantityChange = useCallback(
    async (item: CartModel['items'][number], value: number) => {

      /**
       * Updates the quantity for the cart item in the backend.
       * This function is used internally by processQuantityChange.
       */
      const handleQuantityChange = (item: CartModel['items'][number], quantity: number) => {
        return updateProductsFromCart([
          {
            uid: item.uid,
            quantity,
          },
        ]);
      };

      if (isNaN(value) || item.quantity === value) {
        return;
      }

      // Clear all item update errors before processing
      setItemUpdateErrors(new Map());

      try {
        // Mark item as updating
        setItemsUpdating((prev) => {
          const next = new Map(prev);
          next.set(item.uid, { isUpdating: true, updatedValue: value });
          return next;
        });

        await handleQuantityChange(item, value);

        // Callbacks for item removal or quantity update
        if (value === 0) {
          onItemRemove?.(item);
        } else {
          onQuantityUpdate?.(item, value);
        }
      } catch (error: any) {
        // Remove updating state and set error message
        setItemsUpdating((prev) => {
          const next = new Map(prev);
          next.delete(item.uid);
          return next;
        });
        setItemUpdateErrors((prev) => {
          const next = new Map(prev);
          next.set(item.uid, error.message);
          return next;
        });
      }
    },
    [onItemRemove, onQuantityUpdate]
  );

  /**
   * Debounced version of processQuantityChange (500ms delay).
   * Use this to avoid rapid API calls when the user is typing or clicking quickly.
   */
  const debouncedQuantityChange = useMemo(
    () => debounce(processQuantityChange, 500),
    [processQuantityChange]
  );

  /**
   * Removes the item from the cart (sets quantity to 0).
   * Also updates the UI state to reflect the removal.
   */
  const handleRemoveItem = (item: CartModel['items'][number]) => {
    setItemsUpdating((prev) => {
      const next = new Map(prev);
      next.set(item.uid, { isUpdating: true, updatedValue: 0 });
      return next;
    });
    return updateProductsFromCart([
      {
        uid: item.uid,
        quantity: 0,
      },
    ]).then((data) => {
      onItemRemove?.(item);
      return Promise.resolve(data);
    });
  };

  /**
   * Sets the updating state for a specific cart item.
   * Used to manually mark an item as updating or not.
   */
  const setItemUpdating = (uid: string, state: boolean) => {
    setItemsUpdating((prev) => {
      const next = new Map(prev);
      next.set(uid, { isUpdating: state, updatedValue: state });
      return next;
    });
  };

  /**
   * Sets or clears the update error for a specific cart item.
   * Pass an error string to set, or an empty string to clear.
   */
  const setItemUpdateError = (uid: string, error: string) => {
    setItemUpdateErrors((prev) => {
      const next = new Map(prev);
      if (error) {
        next.set(uid, error);
      } else {
        next.delete(uid);
      }
      return next;
    });
  };

  // Construct the API object to return from the hook
  const api: UseCartItemsApi = {
    showIncludedTaxPrice,
    showExcludingTaxPrice,
    itemsUpdating,
    itemUpdateErrors,
    getConfiguration,
    getPriceProps,
    getSubtotalProps,
    processQuantityChange,
    debouncedQuantityChange,
    setItemsUpdating,
    getWarningMessage,
    setItemUpdateError,
    handleRemoveItem,
    setItemUpdating,
  };

  // Return the API for use in components
  return api;
};